import { useCallback, useRef, useState } from 'react'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import Draggable from 'react-draggable'
import {
  Modal as AntModal,
  Button,
  Col,
  Radio,
  Row,
  Space,
  Spin,
  Tooltip
} from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'

import TextArea from 'antd/lib/input/TextArea'
import { useApi } from 'admin/lib/Api'
import { useParams } from 'react-router-dom'
import { COUNTRY } from '@qflow/constants'
import { colours } from '@qflow/theme'

type FoundAddress = {
  fullAddress: string
  postCode: string
  country: string
  coordinates: {
    lat: number
    lon: number
  }
  score: number
}

export type SelectedAddressResult = {
  entered: string
  known: string
  coordinates: { lat: number; lon: number }
}

export type CurrentValuesResult = {
  given: string
  known: string
}

type SearchState = {
  isSearching: boolean
  hasSearched: boolean
  searchError: string | null
  foundAddresses: FoundAddress[]
}

export type SiteAddressSearchModalProps = {
  currentValue: string
  projectCountry: string
  isOpen: boolean
  onCancel: () => void
  onSubmit: (result: SelectedAddressResult) => void
}

export function SiteAddressSearchModal({
  currentValue,
  projectCountry,
  isOpen,
  onCancel,
  onSubmit
}: SiteAddressSearchModalProps) {
  const [disabled, setDisabled] = useState(true)
  const [isSubmitting] = useState(false)
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const draggleRef = useRef<HTMLDivElement>(null)

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  return (
    <AntModal
      title={
        <StyledTitle
          onMouseOver={() => {
            if (disabled) {
              setDisabled(false)
            }
          }}
          onMouseOut={() => {
            setDisabled(true)
          }}
          // fix eslintjsx-a11y/mouse-events-have-key-events
          // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/mouse-events-have-key-events.md
          onFocus={() => {}}
          onBlur={() => {}}
          // end
        >
          Edit Site Address
        </StyledTitle>
      }
      open={isOpen}
      onCancel={onCancel}
      maskClosable={false}
      confirmLoading={isSubmitting}
      footer={null}
      width={600}
      wrapClassName="address-modal"
      modalRender={modal => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      <SearchAddressPanel
        defaultEnteredValue={currentValue}
        projectCountry={projectCountry}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    </AntModal>
  )
}

function SearchAddressPanel({
  defaultEnteredValue,
  projectCountry,
  onCancel,
  onSubmit
}: {
  defaultEnteredValue: string
  projectCountry: string
  onCancel: () => void
  onSubmit: (result: SelectedAddressResult) => void
}) {
  const [enteredValue, setEnteredValue] = useState<string>(defaultEnteredValue)
  const [searchState, setSearchState] = useState<SearchState>({
    isSearching: false,
    hasSearched: false,
    searchError: null,
    foundAddresses: []
  })
  const [selectedFoundAddress, setSelectedFoundAddress] =
    useState<FoundAddress | null>()

  const api = useApi()

  const { projectId, accountId } = useParams<{
    projectId: string
    accountId: string
  }>()

  const handleSearching = useCallback(() => {
    if (!api) {
      console.warn('api not set')
      return
    }
    const apiGetGeocoding = () => {
      if (!accountId || !projectId) {
        console.warn('Account ID or Project ID not set')
        return
      }
      return api.getAddresses(enteredValue, accountId, projectId)
    }

    setSearchState({
      isSearching: true,
      hasSearched: false,
      searchError: null,
      foundAddresses: []
    })

    async function searchAddresses() {
      const response = await apiGetGeocoding()
      if (!response) {
        setSearchState({
          isSearching: false,
          hasSearched: true,
          searchError: 'Failed to get response',
          foundAddresses: []
        })
        return
      }

      const [foundAddresses, , error] = response
      if (error) {
        setSearchState({
          isSearching: false,
          hasSearched: true,
          searchError: error.toString(),
          foundAddresses: foundAddresses || []
        })
      } else {
        setSearchState({
          isSearching: false,
          hasSearched: true,
          searchError: null,
          foundAddresses: foundAddresses || []
        })
      }
    }

    searchAddresses()
  }, [api, enteredValue, accountId, projectId])

  const handleMatchedResultSelected = useCallback(
    e => {
      const selectedMatchedAddress = searchState.foundAddresses[e.target.value]
      setSelectedFoundAddress(selectedMatchedAddress)
    },
    [searchState.foundAddresses]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedFoundAddress) {
      console.warn('Submit unexpectedly had no selected matched address')
      return
    }
    onSubmit({
      entered: enteredValue,
      known: selectedFoundAddress.fullAddress,
      coordinates: selectedFoundAddress.coordinates
    })
  }, [enteredValue, onSubmit, selectedFoundAddress])

  return (
    <>
      <h1>Search for the address</h1>
      <p>
        Please locate the address by searching for it. More specific inputs will
        be more accurate. Post/Zip codes can be entered.
      </p>

      <details open>
        <summary className="emphasise">
          Better results can be found by...
        </summary>
        <ul>
          <li>Removing units e.g. Unit 5</li>
          <li>Removing block numbers e.g. Block J1</li>
          <li>Removing supplier/contractor</li>
        </ul>
      </details>

      <p className="compress">
        <label className="input-label" htmlFor="addressSearchInput">
          Search address
        </label>
      </p>

      <Row gutter={3} style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <TextArea
            id="addressSearchInput"
            autoSize
            rows={1}
            defaultValue={defaultEnteredValue}
            onChange={e => setEnteredValue(e.target.value)}
          />
        </Col>
        <Col>
          <Button
            tabIndex={-1}
            icon={<Icons.SearchOutlined />}
            onClick={() => handleSearching()}
            aria-label="Search"
          />
        </Col>
      </Row>

      {searchState.isSearching && (
        <Row>
          <Col offset={10} span={4}>
            <Spin />
          </Col>
        </Row>
      )}

      {!searchState.isSearching && searchState.searchError && (
        <p className="error">
          Failed to search addresses: {searchState.searchError}
        </p>
      )}

      {!searchState.isSearching &&
        !searchState.searchError &&
        searchState.hasSearched &&
        searchState.foundAddresses.length === 0 && (
          <p className="warning">No addresses found matching search text</p>
        )}

      {!searchState.isSearching &&
        !searchState.searchError &&
        searchState.foundAddresses.length > 0 && (
          <Radio.Group onChange={handleMatchedResultSelected}>
            <Space direction="vertical">
              {searchState.foundAddresses.map((a, i) => (
                <Radio key={i} value={i}>
                  <AddressSearchResultDisplay
                    foundAddress={a}
                    projectCountry={projectCountry}
                  />
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )}

      <ModalButtonRow>
        <ModalButton label="Cancel" onClick={onCancel} />
        <ModalButton
          label="Update and close"
          onClick={handleSubmit}
          primary
          disabled={!selectedFoundAddress}
        />
      </ModalButtonRow>
    </>
  )
}
function AddressSearchResultDisplay({
  foundAddress,
  projectCountry
}: {
  foundAddress: FoundAddress
  projectCountry: string
}) {
  const matchingUSA = COUNTRY.USA.toLowerCase()

  const alertIfCountryDifferentToProject = useCallback(
    (resultCountry: string) => {
      if (
        resultCountry?.toLowerCase() === projectCountry?.toLowerCase() ||
        (resultCountry?.toLowerCase() === 'united states' &&
          projectCountry?.toLowerCase() === matchingUSA)
      ) {
        return null
      }

      return (
        <Tooltip
          title={`Country '${resultCountry}' is different from project country '${projectCountry}'`}
        >
          <Icons.WarningOutlined
            style={{ color: colours.ERROR, marginLeft: '5px' }}
            title="Warning: Different Country"
          />
        </Tooltip>
      )
    },
    [matchingUSA, projectCountry]
  )
  return (
    <>
      <Tooltip
        title={`${foundAddress.country} [Score: ${Math.round(foundAddress.score * 1000) / 1000}]`}
      >
        <span className="emphasise">{foundAddress.fullAddress}</span>
      </Tooltip>
      {alertIfCountryDifferentToProject(foundAddress.country)}
    </>
  )
}

function ModalButtonRow({ children }: { children: React.ReactNode }) {
  return (
    <Row className="modal-buttons">
      <Col flex="auto"></Col>
      <Col>{children}</Col>
    </Row>
  )
}

function ModalButton({
  label,
  primary,
  disabled,
  onClick
}: {
  label: string
  primary?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const type = primary ? 'primary' : 'default'
  const htmlType = primary ? 'submit' : undefined
  const style = primary ? undefined : { marginRight: '10px' }
  return (
    <Button
      type={type}
      htmlType={htmlType}
      disabled={disabled}
      aria-label={label}
      style={style}
      onClick={onClick}
    >
      {label}
    </Button>
  )
}

const StyledTitle = styled.div`
  width: 100%;
  cursor: move;
`
